import React, { useEffect, useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faUser } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import MatterService from '../../../../../../../AppSettings/services/MatterService';
import EmployeeService from '../../../../../../../AppSettings/services/EmployeeService';
import './ContactsPage.css';
import {
  AssociatedEmployee,
  EmployeeOption,
  Employees,
} from '@frontend/Models/Employee/EmployeeModels';
import { GetMatterByIdResponse } from '@frontend/Models/Matter/MatterRequestResponseModels';
import CardHeader from '@frontend/Components/Titles/CardHeader';
import SecondaryTable from '@frontend/Components/SecondaryTable/SecondaryTable';
import { Column } from '@frontend/Models/Table/TableModels';

// Contact interface for table rows
interface Contact {
  id: string;
  type: 'Leader' | 'Member';
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  employeeid: string;
  canDelete: boolean;
}

const ContactsPage: React.FC = () => {
  const { matterId } = useParams<{ matterId: string }>();
  const [matter, setMatter] = useState<GetMatterByIdResponse | null>(null);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeOption[]>(
    []
  );
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const queryClient = useQueryClient();

  // Fetch matter details using react-query
  const { data, isLoading, isError, error } = useQuery<GetMatterByIdResponse>({
    queryKey: ['Matters', matterId],
    queryFn: () =>
      matterId
        ? MatterService.getMatterDetails(matterId)
        : Promise.reject('No matterId'),
    enabled: !!matterId,
  });

  // Fetch employees using react-query
  const employeeData = useQuery<Employees[]>({
    queryKey: ['Employees'],
    queryFn: EmployeeService.getEmployees,
  });

  // Mutations for adding/removing employees
  const addEmployeesMutation = useMutation({
    mutationFn: (employeeIds: string[]) =>
      matterId
        ? MatterService.updateAssociatedEmployees(
            matterId,
            'add',
            employeeIds
          )
        : Promise.reject('No matterId'),
    onSuccess: () => {
      setSuccessMessage(
        selectedEmployees.length > 1
          ? 'Employees added successfully'
          : 'Employee added successfully'
      );
      setTimeout(() => setSuccessMessage(''), 3000);
      if (matterId) {
        queryClient.invalidateQueries({ queryKey: ['Matters', matterId] });
      }
      setSelectedEmployees([]);
    },
    onError: (err: Error) => {
      setErrorMessage(err.message || 'Failed to add employees');
      setTimeout(() => setErrorMessage(''), 3000);
    },
  });

  const removeEmployeeMutation = useMutation({
    mutationFn: (employeeId: string) =>
      matterId
        ? MatterService.updateAssociatedEmployees(matterId, 'remove', [
            employeeId,
          ])
        : Promise.reject('No matterId'),
    onSuccess: () => {
      setSuccessMessage('Employee removed successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      if (matterId) {
        queryClient.invalidateQueries({ queryKey: ['Matters', matterId] });
      }
    },
    onError: (err: Error) => {
      setErrorMessage(err.message || 'Failed to remove employee');
      setTimeout(() => setErrorMessage(''), 3000);
    },
  });

  useEffect(() => {
    if (data) {
      setMatter(data);
    }
  }, [data]);

  useEffect(() => {
    if (employeeData?.data && matter) {
      // Get IDs of employees already associated with the matter
      const associatedIds = new Set<string>();
      
      // Add the leader's ID
      if (matter.employee_details && matter.employeeid) {
        associatedIds.add(matter.employeeid);
      }
      
      // Add all associated employees' IDs
      if (matter.associated_employees && Array.isArray(matter.associated_employees)) {
        matter.associated_employees.forEach((emp: AssociatedEmployee) => {
          if (emp.employeeid != null) {
            associatedIds.add(emp.employeeid.toString());
          }
        });
      }
      
      // Filter out employees who are already associated
      const availableEmployees = employeeData.data
        .map((employee: Employees) => ({
          value: employee.EmployeeID, // Use the full EmployeeID string (UUID)
          label: `${employee.FirstName} ${employee.LastName}`,
        }))
        .filter((employee) => !associatedIds.has(employee.value));
      
      setEmployees(availableEmployees);
    }
  }, [employeeData?.data, matter]);

  const handleSelectChange = (selectedOptions: readonly EmployeeOption[]) => {
    setSelectedEmployees([...selectedOptions]);
  };

  const handleAddEmployees = () => {
    if (selectedEmployees.length > 0) {
      const employeeIds = selectedEmployees.map((employee) => employee.value);
      addEmployeesMutation.mutate(employeeIds);
    } else {
      setErrorMessage('Please select at least one employee to add');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleRemoveEmployee = (employeeId: string) => {
    if (window.confirm('Are you sure you want to remove this employee?')) {
      removeEmployeeMutation.mutate(employeeId);
    }
  };

  // Transform matter data into Contact array for the table
  const contacts = useMemo((): Contact[] => {
    if (!matter) return [];

    const contactList: Contact[] = [];

    // Add leader
    if (matter.employee_details && matter.employeeid) {
      contactList.push({
        id: `leader-${matter.employeeid}`,
        type: 'Leader',
        firstname: matter.employee_details.firstname,
        lastname: matter.employee_details.lastname,
        // Nullable since Build M — a field member may carry neither.
        email: matter.employee_details.email ?? '',
        phone: matter.employee_details.phone ?? '',
        employeeid: matter.employeeid,
        canDelete: false,
      });
    }

    // Add associated employees
    if (matter.associated_employees && Array.isArray(matter.associated_employees)) {
      matter.associated_employees.forEach((employee: AssociatedEmployee) => {
        if (employee.employeeid == null) return;
        contactList.push({
          id: `member-${employee.employeeid}`,
          type: 'Member',
          firstname: employee.firstname,
          lastname: employee.lastname,
          email: employee.email ?? '',
          phone: employee.phone ?? '',
          employeeid: employee.employeeid.toString(),
          canDelete: true,
        });
      });
    }

    return contactList;
  }, [matter]);

  // Define table columns
  const columns: Column<Contact>[] = [
    {
      header: 'Type',
      render: (row: Contact) => (
        <span className={`contacts-status-badge ${row.type.toLowerCase()}`}>
          {row.type}
        </span>
      ),
    },
    {
      header: 'Name',
      icon: faUser,
      render: (row: Contact) => (
        <span>{`${row.firstname} ${row.lastname}`}</span>
      ),
    },
    {
      header: 'Email',
      accessor: 'email',
    },
    {
      header: 'Phone',
      accessor: 'phone',
    },
    {
      header: 'Actions',
      render: (row: Contact) =>
        row.canDelete ? (
          <button
            className="contacts-delete-btn"
            onClick={() => handleRemoveEmployee(row.employeeid)}
            disabled={removeEmployeeMutation.isPending}
            aria-label="Remove Employee"
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        ) : (
          <span>-</span>
        ),
    },
  ];

  if (isLoading) {
    return <div className="contacts-loading">Loading...</div>;
  }

  if (isError) {
    return (
      <div className="contacts-error">
        <p>Error: {error?.message || 'Something went wrong'}</p>
      </div>
    );
  }

  return (
    <div className="contacts-page-container">
      {/* Card Header */}
      <CardHeader title="Contacts" icon={faUser} />

      {/* Success and Error Messages */}
      {successMessage && (
        <div className="contacts-success-message">{successMessage}</div>
      )}
      {errorMessage && (
        <div className="contacts-error-message">{errorMessage}</div>
      )}

      {/* Employee Dropdown with Add Button */}
      <div className="contacts-dropdown-section">
        <div className="contacts-dropdown-container">
          <Select
            isMulti
            options={employees}
            onChange={handleSelectChange}
            value={selectedEmployees}
            placeholder="Search & Select to add employees..."
            isClearable
            isLoading={employeeData.isLoading}
            className="contacts-select"
            menuPortalTarget={
              typeof window !== 'undefined' ? document.body : null
            }
            menuPosition="fixed"
          />
          <button
            className="contacts-add-btn"
            onClick={handleAddEmployees}
            disabled={
              selectedEmployees.length === 0 || addEmployeesMutation.isPending
            }
          >
            <FontAwesomeIcon icon={faPlus} /> Add{' '}
            {selectedEmployees.length > 0
              ? `(${selectedEmployees.length})`
              : ''}
          </button>
        </div>
      </div>

      {/* Contacts Table using SecondaryTable */}
      <SecondaryTable
        columns={columns}
        data={contacts}
        rowKey="id"
        emptyMessage="No contacts found"
      />
    </div>
  );
};

export default ContactsPage;
